import React, { useEffect, useMemo, useState } from 'react';
import { teacherApi } from '../../services/api';
import FilterPills from '../../components/FilterPills';

const F = { fontFamily: 'Inter' } as const;

// T29: Kütüphane Saati — kurum admininin bu öğretmene atadığı kütüphane
// saatleri, kendi sayfasında (Randevularım'ı kalabalıklaştırmasın diye ayrı).
// Saatler okul saati (TR, UTC+3) — display-only sabit ofset.
const TR_OFFSET = 3 * 3600 * 1000;
const trDate = (iso: string) => new Date(new Date(iso).getTime() + TR_OFFSET);
const trClock = (iso: string) => {
  const d = trDate(iso);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};
const DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const trDayLabel = (iso: string) => {
  const d = trDate(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${DAYS[d.getUTCDay()]}`;
};

interface Duty {
  id: string;
  institution_name: string;
  start_at: string;
  end_at: string;
  capacity: number;
  reserved_count: number;
  students: Array<{ uuid: string | null; name: string; status: 'booked' | 'attended' }>;
}

// Gün / Hafta (Pzt–Paz) / Ay penceresi — öğrenci sayfası ve admin paneliyle
// aynı LibCal kalıbı; tekrarlı atamalarda liste sınırsız uzamasın.
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
type WindowView = 'day' | 'week' | 'month';
const VIEW_LABEL: Record<WindowView, string> = { day: 'Gün', week: 'Hafta', month: 'Ay' };
function windowRange(view: WindowView, anchor: Date): { from: Date; to: Date } {
  if (view === 'day') {
    const d = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    return { from: d, to: d };
  }
  if (view === 'month') {
    return {
      from: new Date(anchor.getFullYear(), anchor.getMonth(), 1),
      to: new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0),
    };
  }
  const dow = anchor.getDay() === 0 ? 6 : anchor.getDay() - 1;
  const from = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - dow);
  const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
  return { from, to };
}

const TeacherLibraryDuties: React.FC = () => {
  const [duties, setDuties] = useState<Duty[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Duty | null>(null);
  const [view, setView] = useState<WindowView>('week');
  const [anchor, setAnchor] = useState(() => new Date());

  const range = useMemo(() => windowRange(view, anchor), [view, anchor]);
  const rangeLabel = view === 'month'
    ? `${MONTHS[range.from.getMonth()]} ${range.from.getFullYear()}`
    : view === 'day'
      ? `${range.from.getDate()} ${MONTHS[range.from.getMonth()]} ${DAYS[range.from.getDay()]}`
      : `${range.from.getDate()} ${MONTHS[range.from.getMonth()]} – ${range.to.getDate()} ${MONTHS[range.to.getMonth()]}`;
  const shift = (dir: -1 | 1) => setAnchor((a) => view === 'month'
    ? new Date(a.getFullYear(), a.getMonth() + dir, 1)
    : new Date(a.getFullYear(), a.getMonth(), a.getDate() + dir * (view === 'day' ? 1 : 7)));

  useEffect(() => {
    setLoading(true);
    teacherApi.libraryDuties(ymd(range.from), ymd(range.to))
      .then((res) => setDuties(res.data ?? []))
      .catch(() => { /* boş liste görünür */ })
      .finally(() => setLoading(false));
  }, [view, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const m = new Map<string, Duty[]>();
    for (const d of duties) {
      const day = trDayLabel(d.start_at);
      if (!m.has(day)) m.set(day, []);
      m.get(day)!.push(d);
    }
    return Array.from(m.entries());
  }, [duties]);

  return (
    <div>
      <div className="mb-4">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Kütüphane Saati</h1>
        <p style={{ ...F, fontSize: 14, fontWeight: 400, color: '#888' }}>
          Kurumunun sana atadığı kütüphane saatleri ve rezervasyon yapan öğrenciler.
        </p>
      </div>

      {/* Gün/Hafta/Ay gezgini */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <button onClick={() => shift(-1)} className="bg-[#fafafa] rounded-[10px] w-9 h-9 text-[18px] text-[#2b2b2b] hover:bg-gray-100" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }} aria-label="Önceki">‹</button>
        <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b' }} className="min-w-[170px] text-center">{rangeLabel}</span>
        <button onClick={() => shift(1)} className="bg-[#fafafa] rounded-[10px] w-9 h-9 text-[18px] text-[#2b2b2b] hover:bg-gray-100" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }} aria-label="Sonraki">›</button>
        <button onClick={() => setAnchor(new Date())} className="bg-[#fafafa] rounded-[10px] px-3 h-9 text-[13px] font-semibold text-[#2b2b2b] hover:bg-gray-100" style={{ ...F, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>Bugün</button>
        <FilterPills<WindowView>
          className="ml-1"
          options={(['day', 'week', 'month'] as const).map((v) => ({ value: v, label: VIEW_LABEL[v] }))}
          value={view}
          onChange={setView}
          tone="blue"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : duties.length === 0 ? (
        <div className="bg-[#fafafa] rounded-[24px] p-10 text-center" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#888' }}>
            {rangeLabel} aralığında sana atanmış kütüphane saati yok. ‹ › ile diğer tarihlere bakabilirsin.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([day, dayDuties]) => (
            <div key={day} className="bg-[#fafafa] rounded-[24px] p-4 xl:p-5" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <p style={{ ...F, fontSize: 16, fontWeight: 700, color: '#2b2b2b' }} className="mb-3">{day}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {dayDuties.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => setDetail(d)}
                    className="bg-white rounded-[14px] p-3 border-l-4 border-[#01cfd6] cursor-pointer hover:shadow-md transition-shadow"
                    style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.08)' }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#2b2b2b' }}>
                        {trClock(d.start_at)} – {trClock(d.end_at)}
                      </span>
                      <span
                        style={{ ...F, fontSize: 12, fontWeight: 700, color: d.reserved_count > 0 ? '#59a05e' : '#888' }}
                      >
                        {d.reserved_count}/{d.capacity} rezervasyon
                      </span>
                    </div>
                    <p style={{ ...F, fontSize: 12, fontWeight: 500, color: '#888' }} className="mb-1">{d.institution_name}</p>
                    {d.students.length > 0 ? (
                      <p style={{ ...F, fontSize: 12, fontWeight: 600, color: '#3973bf' }}>
                        Öğrencileri görüntüle →
                      </p>
                    ) : (
                      <p style={{ ...F, fontSize: 12, fontWeight: 400, color: '#c5c5c5' }}>Henüz rezervasyon yok</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rezervasyon detayı — her slot tıklanabilir; öğrenci listesi kartta
          değil bu popup'ta gösterilir (kart özet kalır, liste kaç kişi olursa
          olsun taşmaz). */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setDetail(null)} />
          <div className="relative bg-[#f7f7f7] rounded-[24px] w-full max-w-[520px] max-h-[80vh] flex flex-col p-6" style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
            <div className="flex items-start justify-between mb-1">
              <h2 style={{ ...F, fontSize: 20, fontWeight: 700, color: '#2b2b2b' }}>Rezervasyonlar</h2>
              <button onClick={() => setDetail(null)} className="w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center text-[18px] text-[#2b2b2b]">✕</button>
            </div>
            <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#888' }} className="mb-4">
              {trDayLabel(detail.start_at)} · {trClock(detail.start_at)} – {trClock(detail.end_at)} · {detail.reserved_count}/{detail.capacity} rezervasyon
            </p>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1">
              {detail.students.length === 0 ? (
                <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#c5c5c5' }} className="text-center py-6">
                  Henüz rezervasyon yok.
                </p>
              ) : (
                detail.students.map((s, i) => (
                  <div key={s.uuid ?? i} className="bg-white rounded-[10px] px-4 py-2.5 flex items-center justify-between">
                    <span style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>{i + 1}. {s.name}</span>
                    <span
                      className={`px-2 py-0.5 rounded-[8px] ${s.status === 'attended' ? 'bg-green-100' : 'bg-[#3973bf]/10'}`}
                      style={{ ...F, fontSize: 11, fontWeight: 600, color: s.status === 'attended' ? '#59a05e' : '#3973bf' }}
                    >
                      {s.status === 'attended' ? 'Katıldı' : 'Rezerve'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherLibraryDuties;
